import { UserPhoto } from "@/src/models/user.model";
import { BlurView } from "expo-blur";
import {
  ArrowLeft2,
  ArrowRight2,
  CloseCircle,
  Trash,
} from "iconsax-react-nativejs";
import React, { useCallback, useEffect, useState } from "react";
import {
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StatusBar,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useTheme } from "@/src/styles/theme-provider";

const MAX_DOTS = 20;

type LigntBoxProps = Readonly<{
  photos: readonly UserPhoto[];
  index: number;
  onClose: () => void;
  onDelete?: (photo: UserPhoto, index: number) => void;
  children?: React.ReactNode;
}>;

const Lightbox = ({
  photos,
  index,
  onClose,
  onDelete,
  children,
}: LigntBoxProps) => {
  const [currentIndex, setCurrentIndex] = useState(index);
  const { themeVars, varStyle } = useTheme();

  const currentPhoto = photos[currentIndex];
  const totalPhotos = photos.length;
  const counterLabel = `${currentIndex + 1} / ${totalPhotos}`;
  const remainingPhotos = totalPhotos - MAX_DOTS;
  const hasMoreThanMaxDots = totalPhotos > MAX_DOTS;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalPhotos - 1;

  const goToPrevious = useCallback(() => {
    if (!isFirst) setCurrentIndex((prev) => prev - 1);
  }, [isFirst]);

  const goToNext = useCallback(() => {
    if (!isLast) setCurrentIndex((prev) => prev + 1);
  }, [isLast]);

  const goToIndex = (i: number) => setCurrentIndex(i);

  // On web, drive the gallery from the keyboard: ←/→ to step through photos and
  // Esc to close, mirroring the on-screen arrow buttons.
  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goToPrevious();
      else if (e.key === "ArrowRight") goToNext();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goToPrevious, goToNext, onClose]);

  const handleDelete = () => {
    if (!onDelete || !currentPhoto) return;
    onDelete(currentPhoto, currentIndex);
    if (totalPhotos === 1) {
      onClose();
    } else if (isLast) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  return (
    <Modal
      visible
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <StatusBar barStyle="light-content" />
      <Pressable style={varStyle} className="flex-1" onPress={onClose}>
        <BlurView intensity={15} tint="dark" className="flex-1">
          <View className="flex-row justify-between items-center mt-12 mx-4 mb-2">
            <Text className="text-white/60 text-xs font-medium tracking-widest">
              {counterLabel}
            </Text>
            <View className="flex-row items-center gap-3">
              {onDelete && (
                <TouchableOpacity
                  onPress={handleDelete}
                  activeOpacity={0.7}
                  className="bg-red-400 rounded-full p-1 hover:opacity-90"
                >
                  <Trash size={20} color="white" />
                </TouchableOpacity>
              )}
              <TouchableOpacity onPress={onClose} activeOpacity={0.7} className="p-1.5 rounded-full hover:bg-ash-3">
                <CloseCircle size={26} color={themeVars["--color-slate-default"] as string} />
              </TouchableOpacity>
            </View>
          </View>

          {/* The image fills the available space and `contain`s itself, so it
              stays responsive at any screen size / aspect ratio (the old fixed
              3:1 box squashed photos on mobile). Arrows overlay the edges via an
              absolute layer so they never steal width from the image. */}
          <View className="flex-1">
            <Pressable
              className="flex-1 items-center justify-center px-2 md:px-4"
              onPress={onClose}
            >
              <Pressable
                className="w-full h-full items-center justify-center"
                onPress={(e) => e.stopPropagation()}
              >
                <Image
                  source={{ uri: currentPhoto?.url }}
                  className="w-full h-full rounded-xl"
                  resizeMode="contain"
                />
              </Pressable>
            </Pressable>

            <View
              pointerEvents="box-none"
              className="absolute inset-0 flex-row items-center justify-between px-3 md:px-4"
            >
              <TouchableOpacity
                onPress={goToPrevious}
                disabled={isFirst}
                activeOpacity={0.7}
                className={isFirst ? "opacity-30" : ""}
              >
                <View className="w-10 h-10 rounded-full bg-white/80 items-center justify-center hover:bg-ash-3">
                  <ArrowLeft2 size={20} color={themeVars["--color-slate-default"] as string} />
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={goToNext}
                disabled={isLast}
                activeOpacity={0.7}
                className={isLast ? "opacity-30" : ""}
              >
                <View className="w-10 h-10 rounded-full bg-white/80 items-center justify-center hover:bg-ash-3">
                  <ArrowRight2 size={20} color={themeVars["--color-slate-default"] as string} />
                </View>
              </TouchableOpacity>
            </View>
          </View>

          <Pressable
            className="pb-10 pt-3 items-center gap-3"
            onPress={onClose}
          >
            <Text className="text-white/40 text-xs" numberOfLines={1}>
              {currentPhoto?.file_name}
            </Text>
            <Pressable onPress={(e) => e.stopPropagation()}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerClassName="justify-center gap-1.5 px-4"
              >
                {photos.slice(0, MAX_DOTS).map((_, i) => (
                  <TouchableOpacity
                    key={i}
                    onPress={() => goToIndex(i)}
                    activeOpacity={0.7}
                    className="p-1 rounded-full hover:bg-ash-3"
                  >
                    <View
                      className={`h-1.5 rounded-full ${i === currentIndex ? "bg-white w-4" : "bg-white/30 w-1.5"}`}
                    />
                  </TouchableOpacity>
                ))}
                {hasMoreThanMaxDots && (
                  <Text className="text-white/30 text-xs self-center ml-1">
                    +{remainingPhotos}
                  </Text>
                )}
              </ScrollView>
            </Pressable>
          </Pressable>
          {children}
        </BlurView>
      </Pressable>
    </Modal>
  );
};

export default Lightbox;
